import { useMediaQuery } from "@mui/material";
import {
  PRODUCT_GALLERY_MAX_ROW_HEIGHT,
  PRODUCT_GALLERY_MIN_ROW_HEIGHT,
  SIDE_DRAWER_DEFAULT_WIDTH
} from "../utils/constants";

/* eslint-disable no-magic-numbers */

/**
 * Interface for the return object which gets supplied via this hook.
 */
export interface IResponsiveSizes {
  fontSize: string;
  iconPadding: string;
  iconMargin: string;
  panelHeight: string;
  loginModalHeight: string;
  loginModalWidth: string;
  loginModalPadding: string;
  logoutModalPosition: string;
  singleSelectMenuTop: string;
  multiSelectMenuTop: string;
  multiSelectMenuLeft: string;
  multiSelectMenuHeight: number;
  buildingLevelDropdownTop: string;
  toolbarTop: string;
  toolbarButtonFontSize: string;
  toolbarButtonSpacing: string;
  toolbarPadding: string;
  toolbarPlusButtonLeftMargin: string;
  toolbarPlusButtonTop: string;
  toolbarIconSize: string;
  buildingLevelDropdownMaxHeight: string;
  isLandscape: boolean;
  isMobileDevice: boolean;
  isWidthOver500: boolean;
  isWidthOver700: boolean;
  isWidthOver900: boolean;
  isWidthOver1100: boolean;
  isWidthOver1300: boolean;
  isWidthOver1600: boolean;
  isWidthOver1800: boolean;
  isHeightOver500: boolean;
  isHeightOver650: boolean;
  isHeightOver800: boolean;
  isHeightOver1000: boolean;
  isHeightOver1200: boolean;
  isHeightOver1400: boolean;
  screenHeight: number;
  screenWidth: number;
  innerWindowHeight: number;
  innerWindowWidth: number;
  vMin: number;
  galleryRowHeight: number;
  sideDrawerWidth: number;
  productTooltipWidth: number;
  productSearchTitleMargin: string;
  headerHeight: number;
}

/**
 * Utility hook to utilize the media queries in multiple components without re-requesting them every time.
 * Will be necessary for all of the non-MUI (sub-)components that have to resize according to the screen resolution, as well.
 * Also useful if the components have to resize at different rates than the predefined MUI breakpoints allow for.
 * @returns object containing the predefined values according to the current screen resolution.
 */
/* eslint-disable-next-line complexity */
const useResponsiveSizes = (): IResponsiveSizes => {
  let fontSize = "";
  let iconPadding = "16% 5%";
  let iconMargin = "2%";
  let panelHeight = "12rem";
  let loginModalHeight = "38vh";
  let loginModalWidth = "25vw";
  let loginModalPadding = "3rem 6.5rem 3rem";
  let logoutModalPosition = "88%";
  let singleSelectMenuTop = "";
  let multiSelectMenuTop = "";
  let multiSelectMenuLeft = "4.2rem";
  let multiSelectMenuHeight = 0;
  let toolbarTop = "50%";
  let toolbarButtonFontSize = "inherit";
  let toolbarButtonSpacing = "2";
  let toolbarPadding = ".4rem";
  let toolbarPlusButtonTop = "calc(calc(50vh - 6rem) - calc(2.7 * 1.75rem))";
  let toolbarPlusButtonLeftMargin = ".6rem";
  const toolbarIconSize = "1.75rem";
  let buildingLevelDropdownTop = "50%";
  let buildingLevelDropdownMaxHeight = "85vh";
  const screenWidth = screen.width;
  const screenHeight = screen.height;
  const innerWindowHeight = window.innerHeight;
  const innerWindowWidth = window.innerWidth;
  const outerWindowHeight = window.outerHeight;
  const outerWindowWidth = window.outerWidth;
  let headerHeight = 64;
  const vMin =
    screenHeight / 100 < screenWidth / 100
      ? screenHeight / 100
      : screenWidth / 100;
  const isWidthOver1800 = useMediaQuery("(min-width:1800px)");
  const isWidthOver1600 = useMediaQuery("(min-width:1600px)");
  const isWidthOver1300 = useMediaQuery("(min-width:1300px)");
  const isWidthOver1100 = useMediaQuery("(min-width:1100px)");
  const isWidthOver900 = useMediaQuery("(min-width:900px)");
  const isWidthOver700 = useMediaQuery("(min-width:700px)");
  const isWidthOver500 = useMediaQuery("(min-width:500px)");
  const isHeightOver1400 = useMediaQuery("(min-height:1400px)");
  const isHeightOver1200 = useMediaQuery("(min-height:1200px)");
  const isHeightOver1000 = useMediaQuery("(min-height:1000px)");
  const isHeightOver800 = useMediaQuery("(min-height:800px)");
  const isHeightOver650 = useMediaQuery("(min-height:650px)");
  const isHeightOver500 = useMediaQuery("(min-height:500px)");
  const isLandscape = useMediaQuery("(orientation:landscape)");
  let galleryRowHeight = 120;
  let sideDrawerWidth = SIDE_DRAWER_DEFAULT_WIDTH;
  const productTooltipWidth = screenWidth / 4;
  let productSearchTitleMargin = "1.5rem";
  // Check if the user agent string contains some reference to a mobile device.
  /*Const mobileDeviceUserAgentString = window.navigator.userAgent
        .toLowerCase()
        .includes("mobi");*/
  // Combine this with the check for small screen sizes.
  const isMobileDevice = !isWidthOver900 || !isHeightOver800;
  /* NON ORIENTATION RELATED */
  if (isWidthOver1800) {
    logoutModalPosition = "91%";
    fontSize = "4rem";
  } else if (isWidthOver1600) {
    logoutModalPosition = "89%";
    fontSize = "3.6rem";
  } else if (isWidthOver1300) {
    logoutModalPosition = "87%";
    fontSize = "3.2rem";
  } else if (isWidthOver1100) {
    logoutModalPosition = "85%";
    loginModalWidth = "35vw";
    fontSize = "2.9rem";
    panelHeight = "10.5rem";
  } else if (isWidthOver900) {
    logoutModalPosition = "82%";
    loginModalWidth = "40vw";
    loginModalPadding = "3rem 4rem 3rem";
    fontSize = "2.7rem";
    sideDrawerWidth = 275;
  } else if (isWidthOver700) {
    logoutModalPosition = "78%";
    loginModalWidth = "55vw";
    loginModalPadding = "3rem 3.5rem 3rem";
    fontSize = "2.6rem";
    iconPadding = "16% 5%";
    iconMargin = "1.5%";
    sideDrawerWidth = 250;
  } else if (isWidthOver500) {
    headerHeight = 56;
    logoutModalPosition = "68%";
    loginModalWidth = "65vw";
    loginModalPadding = "3rem 2.5rem 3rem";
    fontSize = "2.5rem";
    panelHeight = "11rem";
    iconPadding = "16% 5%";
    iconMargin = "1%";
    sideDrawerWidth = 225;
  } else {
    headerHeight = 56;
    // Screen width < 500px
    logoutModalPosition = "62%";
    loginModalWidth = "75vw";
    loginModalPadding = "3rem 1.5rem 3rem";
    fontSize = "2.4rem";
    panelHeight = "10rem";
    iconPadding = "16% 5%";
    iconMargin = ".5%";
    sideDrawerWidth = 200;
  }
  if (!isHeightOver500) {
    productSearchTitleMargin = ".3rem";
    multiSelectMenuHeight = screenHeight / 2.5;
    multiSelectMenuTop = "55%";
    singleSelectMenuTop = "11%";
    toolbarTop = "58%";
    toolbarButtonFontSize = "inherit";
    toolbarButtonSpacing = ".25rem";
    toolbarPadding = ".3rem";
    toolbarPlusButtonTop = "calc(calc(50vhh - 6rem) - calc(2.7 * 2.1rem))";
    toolbarPlusButtonLeftMargin = "4.5rem";
    buildingLevelDropdownTop = "25%";
    buildingLevelDropdownMaxHeight = "65vh";
  } else if (!isHeightOver650) {
    productSearchTitleMargin = ".5rem";
    multiSelectMenuHeight = screenHeight / 2.3;
    multiSelectMenuTop = "50%";
    singleSelectMenuTop = "11%";
    toolbarTop = "54.5%";
    toolbarButtonFontSize = "inherit";
    toolbarButtonSpacing = ".25rem";
    toolbarPadding = ".5rem";
    toolbarPlusButtonTop = "calc(calc(50vh - 6rem) - calc(2.7 * 2.1rem))";
    toolbarPlusButtonLeftMargin = "5rem";
    buildingLevelDropdownTop = "30%";
    buildingLevelDropdownMaxHeight = "65vh";
  } else if (!isHeightOver800) {
    productSearchTitleMargin = ".75rem";
    multiSelectMenuHeight = screenHeight / 1.9;
    multiSelectMenuTop = "55%";
    singleSelectMenuTop = "15%";
    toolbarTop = "52.5%";
    toolbarButtonFontSize = "inherit";
    toolbarPadding = ".5rem";
    toolbarButtonSpacing = ".25rem";
    toolbarPlusButtonTop = "calc(calc(50vh - 6rem) - calc(2.7 * 2.1rem))";
    toolbarPlusButtonLeftMargin = "5rem";
    buildingLevelDropdownTop = "30%";
    buildingLevelDropdownMaxHeight = "65vh";
  } else if (!isHeightOver1000) {
    productSearchTitleMargin = ".9rem";
    multiSelectMenuHeight = screenHeight / 1.7;
    multiSelectMenuTop = "20%";
    singleSelectMenuTop = "21%";
    toolbarPadding = ".5rem";
    toolbarButtonSpacing = ".25rem";
    toolbarPlusButtonTop = "calc(calc(50vh - 6rem) - calc(2.7 * 2.1rem))";
    toolbarPlusButtonLeftMargin = "5rem";
    buildingLevelDropdownTop = "38%";
    buildingLevelDropdownMaxHeight = "55vh";
  } else if (!isHeightOver1200) {
    productSearchTitleMargin = "1.1rem";
    multiSelectMenuHeight = screenHeight / 1.6;
    toolbarPlusButtonLeftMargin = "5rem";
    multiSelectMenuTop = "39%";
    singleSelectMenuTop = "27%";
    buildingLevelDropdownTop = "38%";
    buildingLevelDropdownMaxHeight = "55vh";
  } else if (!isHeightOver1400) {
    productSearchTitleMargin = "1.3rem";
    multiSelectMenuHeight = screenHeight / 1.5;
    toolbarPlusButtonLeftMargin = "5rem";
    multiSelectMenuTop = "40%";
    singleSelectMenuTop = "30%";
    buildingLevelDropdownTop = "42%";
    buildingLevelDropdownMaxHeight = "50vh";
  } else {
    multiSelectMenuHeight = screenHeight / 1.4;
    // Screen height > 1400px
    toolbarPlusButtonLeftMargin = "5rem";
    multiSelectMenuTop = "45%";
    singleSelectMenuTop = "31%";
    buildingLevelDropdownTop = "45%";
    buildingLevelDropdownMaxHeight = "45vh";
  }
  /* ORIENTATION RELATED */
  if (isLandscape) {
    if (!isHeightOver500) {
      loginModalHeight = "80vh";
      loginModalPadding = "2.5rem 6.5rem 1rem";
      panelHeight = "6rem";
      fontSize = "2.5rem";
      iconPadding = "16% 5%";
      iconMargin = "1%";
    } else if (!isHeightOver650) {
      loginModalHeight = "65vh";
      loginModalPadding = "2.5rem 6.5rem 1.5rem";
      panelHeight = "7rem";
      fontSize = "3rem";
      iconPadding = "16% 5%";
      iconMargin = "1%";
    } else if (!isHeightOver800) {
      loginModalHeight = "56vh";
      loginModalPadding = "3rem 6.5rem 2rem";
      panelHeight = "9rem";
      fontSize = "3.5rem";
    } else if (!isHeightOver1000) {
      loginModalHeight = "52vh";
      loginModalPadding = "3rem 6.5rem 2.5rem";
      panelHeight = "10.5rem";
      fontSize = "3.8rem";
    }
  }
  /* DEVICE RELATED */
  if (isMobileDevice) {
    multiSelectMenuLeft = "50%";
  } else {
    multiSelectMenuLeft = "4.2rem";
    //MultiSelectMenuHeight = "fit-content";
  }

  // If the calculated image gallery row height does not conform to our boundaries, we adjust the value.
  if (galleryRowHeight >= PRODUCT_GALLERY_MAX_ROW_HEIGHT) {
    galleryRowHeight = PRODUCT_GALLERY_MAX_ROW_HEIGHT;
  } else if (galleryRowHeight <= PRODUCT_GALLERY_MIN_ROW_HEIGHT) {
    galleryRowHeight = PRODUCT_GALLERY_MIN_ROW_HEIGHT;
  }
  const returnObject = {
    fontSize: fontSize,
    iconPadding: iconPadding,
    iconMargin: iconMargin,
    panelHeight: panelHeight,
    loginModalHeight: loginModalHeight,
    loginModalWidth: loginModalWidth,
    loginModalPadding: loginModalPadding,
    logoutModalPosition: logoutModalPosition,
    singleSelectMenuTop: singleSelectMenuTop,
    multiSelectMenuTop: multiSelectMenuTop,
    multiSelectMenuLeft: multiSelectMenuLeft,
    multiSelectMenuHeight: multiSelectMenuHeight,
    buildingLevelDropdownTop: buildingLevelDropdownTop,
    toolbarTop: toolbarTop,
    toolbarButtonFontSize: toolbarButtonFontSize,
    toolbarButtonSpacing: toolbarButtonSpacing,
    toolbarPadding: toolbarPadding,
    toolbarPlusButtonTop: toolbarPlusButtonTop,
    toolbarPlusButtonLeftMargin: toolbarPlusButtonLeftMargin,
    toolbarIconSize: toolbarIconSize,
    buildingLevelDropdownMaxHeight: buildingLevelDropdownMaxHeight,
    isLandscape: isLandscape,
    isMobileDevice: isMobileDevice,
    isWidthOver500: isWidthOver500,
    isWidthOver700: isWidthOver700,
    isWidthOver900: isWidthOver900,
    isWidthOver1100: isWidthOver1100,
    isWidthOver1300: isWidthOver1300,
    isWidthOver1600: isWidthOver1600,
    isWidthOver1800: isWidthOver1800,
    isHeightOver500: isHeightOver500,
    isHeightOver650: isHeightOver650,
    isHeightOver800: isHeightOver800,
    isHeightOver1000: isHeightOver1000,
    isHeightOver1200: isHeightOver1200,
    isHeightOver1400: isHeightOver1400,
    screenHeight: screenHeight,
    screenWidth: screenWidth,
    innerWindowHeight: innerWindowHeight,
    innerWindowWidth: innerWindowWidth,
    outerWindowHeight: outerWindowHeight,
    outerWindowWidth: outerWindowWidth,
    vMin: vMin,
    galleryRowHeight: galleryRowHeight,
    sideDrawerWidth: sideDrawerWidth,
    productTooltipWidth: productTooltipWidth,
    productSearchTitleMargin: productSearchTitleMargin,
    headerHeight: headerHeight,
  };
  return returnObject;
};

export default useResponsiveSizes;
